import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Icons } from '@/components/ui/icons';

import { Skeleton } from '@/components/ui/skeleton';
import { type ThreadItem } from '@/types/messages';

const ProviderMessagesPage = () => {
  const threads = useQuery(api.app.messages.queries.listMyThreads, {});
  const markRead = useMutation(api.app.messages.mutations.markThreadRead);
  const [showUnread, setShowUnread] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const isLoading = threads === undefined;
  const allThreads = (threads || []) as ThreadItem[];
  const filtered = allThreads.filter((t) => (!showUnread || t.unread) && (!search || (t.duty?.title || '').toLowerCase().includes(search.toLowerCase())));

  return (
    <div className='bg-background'>
      {/* Header */}
      <div className='border-b border-border'>
        <div className='px-4 sm:px-6 py-3 sm:py-4 max-w-6xl mx-auto'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex items-center gap-3'>
              <h1 className='text-lg sm:text-xl font-semibold text-foreground tracking-tight'>Nachrichten</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='px-4 sm:px-6 py-5 sm:py-6 max-w-6xl mx-auto'>
        {/* Filters */}
        <div className='flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5'>
          <div className='relative flex-1 max-w-sm'>
            <Icons.Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input placeholder='Dienst suchen...' value={search} onChange={(e) => setSearch(e.target.value)} className='pl-9' />
          </div>
          <div className='flex items-center gap-2'>
            <Switch id='unread-filter' checked={showUnread} onCheckedChange={setShowUnread} />
            <Label htmlFor='unread-filter' className='text-sm text-muted-foreground cursor-pointer'>
              Nur ungelesen
            </Label>
          </div>
        </div>

        {/* Thread list */}
        {isLoading ? (
          <div className='space-y-2'>
            {[0, 1, 2].map((i) => (
              <div key={i} className='flex items-center gap-4 p-4 rounded-lg border'>
                <Skeleton className='h-10 w-10 rounded-md' />
                <div className='flex-1 space-y-2'>
                  <Skeleton className='h-4 w-2/5' />
                  <Skeleton className='h-3 w-1/4' />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className='space-y-2'>
            {filtered.map((t) => (
              <Link key={t.threadId} to='/provider/service/$id' params={{ id: String(t.duty?._id || t.threadId) }} className='block group'>
                <div className='flex items-center gap-3 p-3 sm:p-4 rounded-lg border transition-colors hover:border-primary/30'>
                  <div className='flex items-center justify-center w-10 h-10 rounded-md bg-muted flex-shrink-0'>
                    <Icons.MessageSquare className='h-4 w-4 text-muted-foreground' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <span className='text-sm font-medium text-foreground truncate'>{t.duty?.title || 'Ohne Titel'}</span>
                      {t.unread && (
                        <Badge variant='default' className='text-xs px-1.5 py-0'>
                          Neu
                        </Badge>
                      )}
                    </div>
                    {t.duty?.city && <p className='text-xs text-muted-foreground mt-0.5'>{t.duty.city}</p>}
                  </div>
                  <div className='flex items-center gap-2 flex-shrink-0'>
                    {t.unread && (
                      <Button
                        variant='ghost'
                        size='sm'
                        className='text-xs text-muted-foreground hover:text-foreground'
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          markRead({ threadId: t.threadId });
                        }}
                      >
                        Gelesen
                      </Button>
                    )}
                    <Icons.ChevronRight className='h-4 w-4 text-muted-foreground/50 group-hover:text-primary transition-colors' />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className='rounded-lg border border-dashed border-border py-10 text-center'>
            <Icons.MessageSquare className='size-8 text-muted-foreground/30 mx-auto mb-3' />
            <p className='text-sm text-muted-foreground'>{showUnread ? 'Keine ungelesenen Nachrichten' : 'Keine Nachrichten'}</p>
            <p className='text-xs text-muted-foreground/60 mt-1 max-w-xs mx-auto'>Nachrichten werden automatisch erstellt, sobald Sie einen Dienst besetzen.</p>
            <Button variant='outline' size='sm' className='mt-4 gap-1.5' render={<Link to='/provider/services' />}>
              <Icons.Briefcase className='size-3.5' />
              Dienste anzeigen
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_protected/provider/messages')({
  component: ProviderMessagesPage,
});
